/**************
 LP Tabs
 ***************/

/*
 Real tabbed panels for landing-page sections. Two sections need them (the topic-filtered review
 grid and the Us vs. Them comparison table), so the behaviour lives here once.

 The theme's existing sections/section-pdp-tabs-v1.liquid is NOT this: it renders a pill bar whose
 "tabs" are anchor links, with the active one chosen by a static schema setting and no JS at all.
 Nothing in the theme switches panels, so this is net new.

 Implements the ARIA tabs pattern: roving tabindex (only the selected tab is reachable by Tab, then
 arrows move between them), plus Home/End. Panels are toggled with the `hidden` attribute so
 assistive tech and in-page find both skip the inactive ones.

 Markup contract — see sections/section-lp-review-tabs-v1.liquid for a worked example:

   <div data-lp-tabs>
     <div role="tablist" aria-label="...">
       <button type="button" role="tab" id="x-tab-0" aria-controls="x-panel-0" aria-selected="true">…</button>
     </div>
     <div role="tabpanel" id="x-panel-0" aria-labelledby="x-tab-0">…</div>
   </div>

 The first tab is selected on init regardless of what the markup claims, so a section can render
 every panel visible for no-JS and search engines and let this take over.
*/

window.lpTabs = window.lpTabs || (function () {
    "use strict";

    function activate(group, index, moveFocus) {
        const tabs = group.tabs;
        const panels = group.panels;

        if (index < 0 || index >= tabs.length) {
            return;
        }

        tabs.forEach(function (tab, i) {
            const selected = i === index;
            tab.setAttribute("aria-selected", selected ? "true" : "false");
            // roving tabindex: Tab reaches the tablist once, arrows move within it
            tab.setAttribute("tabindex", selected ? "0" : "-1");
            tab.classList.toggle("active", selected);
        });

        panels.forEach(function (panel, i) {
            panel.hidden = i !== index;
        });

        group.index = index;

        if (moveFocus) {
            tabs[index].focus();
        }
    }

    function bind(container) {
        if (container.dataset.lpTabsBound) {
            return;
        }
        container.dataset.lpTabsBound = "true";

        const tablist = container.querySelector('[role="tablist"]');
        if (!tablist) {
            return;
        }

        // scoped to this container so nested tab groups can't steal each other's tabs
        const tabs = Array.from(tablist.querySelectorAll('[role="tab"]'));
        const panels = tabs
            .map(tab => container.querySelector(`#${window.CSS && CSS.escape ? CSS.escape(tab.getAttribute("aria-controls")) : tab.getAttribute("aria-controls")}`))
            .filter(Boolean);

        // a mismatch means the section rendered a tab without its panel; bail rather than
        // half-wire it and leave content unreachable
        if (!tabs.length || tabs.length !== panels.length) {
            console.warn("LP tabs: tab/panel count mismatch, leaving all panels visible", container);
            return;
        }

        const group = {tabs: tabs, panels: panels, index: 0};

        tabs.forEach(function (tab, i) {
            tab.addEventListener("click", () => activate(group, i, false));

            tab.addEventListener("keydown", function (event) {
                let target = null;
                switch (event.key) {
                    case "ArrowRight":
                    case "ArrowDown":
                        target = (group.index + 1) % tabs.length;
                        break;
                    case "ArrowLeft":
                    case "ArrowUp":
                        target = (group.index - 1 + tabs.length) % tabs.length;
                        break;
                    case "Home":
                        target = 0;
                        break;
                    case "End":
                        target = tabs.length - 1;
                        break;
                    default:
                        return;
                }
                event.preventDefault();
                activate(group, target, true);
            });
        });

        activate(group, 0, false);
    }

    function init(root) {
        (root || document).querySelectorAll("[data-lp-tabs]").forEach(bind);
    }

    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => init(document));
    } else {
        init(document);
    }

    document.addEventListener("shopify:section:load", event => init(event.target));

    return {init: init};
})();
